<script lang="ts">
	import { get } from 'svelte/store';
	//Formating
	import { format } from 'svelte-i18n';
	//Stores
	import { user_data } from 'src/stores/account/user';
import { devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { rooms } from 'src/stores/lists/rooms';
	//Helpers
	import { load_image_from } from '../../helpers/interface_helpers';
	import { convert, convertCost, sortOn } from '../../helpers/utilities';
	import { getMonthlyReportByDevices, getMonthlyReportByPeakPower } from './consumption_helper';
	import { getApplianceTypes } from 'src/helpers/device_settings_helpers';
	import { convertDateFormat, time_zones } from 'src/helpers/date_helper';
	// Design system components
	import {
		ControlsContainer,
		ListItem,
		Scroll,
		Stack,
		SystemStyles,
		Text,
	} from '@Shelly/shelly-design-system';
	// Types
	import type { RequestMonthlyReport } from 'src/types/requests/charts/get_stats_period';
	import { setSidePanelState } from '../Sidepanel/sidepanel-helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let result: RequestMonthlyReport,
		type: 'device' | 'room' | 'purpose' | 'peakPower',
		currency: string = '';

	// todo add the keys below chartTitles

	const unit = 'Wh',
		totalDataKey = {
			device: 'totalByDevice',
			room: 'totalByRoom',
			purpose: 'totalByPurpose',
			peakPower: 'peakPower',
		},
		chartTitles = {
			device: {
				key: 'monthly_report_consumption_by_device',
				default: 'Consumption By Device',
			},
			room: {
				key: 'monthly_report_consumption_by_room',
				default: 'Consumption By Room',
			},
			purpose: {
				key: 'text_energy_by_appliance_type',
				default: 'Energy By Appliance Type',
			},
			peakPower: {
				key: 'monthly_report_consumed_by_peak_power',
				default: 'Consumed / Returned By Peak Power',
			},
		},
		consumptionData = [],
		reversedData = [],
		items =
			type == 'purpose'
				? getApplianceTypes()
				: get(/^(peakPower|device)$/.test(type) ? devices : rooms),
		onDeviceClick = (id: string) => {
			setSidePanelState({
				id,
				type: 'device',
				open: true,
			});
		};
	let dataSet = result?.report?.[totalDataKey[type]];
	if (type == 'device') {
		dataSet = getMonthlyReportByDevices(dataSet, items);
	} else if (type == 'peakPower') {
		dataSet = getMonthlyReportByPeakPower(dataSet, items);
	}
	dataSet = sortOn(dataSet, 'consumption', true);
	if (typeof dataSet == 'undefined') {
		dataSet = [];
	}
	if (type == 'purpose') {
		for (let i = 0; i < dataSet.length; ++i) {
			const item = dataSet[i],
				purpose = items.find((i) => i.usage == item.purpose);
			if (purpose) {
				consumptionData.push({
					id: item.id,
					title: purpose.title,
					color: purpose.color,
					type: 'bar',
					icon: `fas fa-${purpose.icon}`,
					consumption: item.consumption,
					consumptionTitle: convert(item.consumption, unit),
					convertCost: item?.cost ? convertCost(item.cost, currency) : null,
					actionBtn: false,
				});
			}
		}
	} else {
		for (let i = 0; i < dataSet.length; ++i) {
			const item = dataSet[i],
				id = item.id,
				title =
					items[id]?.name ||
					$format('label_unidentified_id', {
						default: 'Unidentified id: {id}',
						values: { id },
					});
			if (id && id != '-999') {
				let image = items[id]?.image || '';
				let time = null;
				if (item?.time) {
					const settings = devices_settings.getByRealId(id);
					const tz = time_zones.includes(settings?.timezone || settings?.sys?.location?.tz)
						? settings?.timezone || settings?.sys?.location?.tz
						: $user_data.timezone;
					time = ` ( ${convertDateFormat(item.time * 1000, tz)} )`;
				}
				if (image) image = load_image_from(image);
				if (item?.consumption) {
					consumptionData.push({
						id,
						title,
						image,
						backgroundColor: item.backgroundColor,
						time,
						color: /^(peakPower|device|room)$/.test(type) ? 'primary' : 'info',
						consumption: item.consumption,
						consumptionTitle: convert(item.consumption, unit),
						cost: item?.cost || 0,
						convertCost: item?.cost ? convertCost(item.cost, currency) : null,
						actionBtn: items[id] != null && /^(peakPower|device)$/.test(type),
					});
				}
				if (/^(peakPower|device)$/.test(type) && item?.reversed) {
					reversedData.push({
						id,
						title,
						image,
						backgroundColor: item.backgroundColor,
						time,
						reversed: item?.reversed ? Math.abs(item.reversed) : 0,
						reversedTitle: convert(Math.abs(item?.reversed || 0), unit),
						actionBtn: items[id] != null && /^(peakPower|device)$/.test(type),
					});
				}
			}
		}
	}
</script>

{#if consumptionData.length || reversedData.length}
	<ControlsContainer color={$showNewLayout ? 'color-1' : 'color-2'}>
		<svelte:fragment slot="first">
			<Text class="text-bold mb-m"
				>{$format(chartTitles[type].key, {
					default: chartTitles[type].default,
				})}</Text
			>
			<Scroll style="height:auto; max-height:270px">
				<Stack gap="s">
					{#each consumptionData as device}
						{#if device.consumption > 0}
							<ListItem
								uniqueId={device.id}
								title={device.title}
								image={device.image}
								backgroundColor={device.backgroundColor}
								icon={device.icon}
								viewType={type == 'purpose' ? 'bar' : 'default'}
								subtitle={device.time}
								extraText={{
									title: `${device.convertCost ? device.convertCost : ''}`,
									color: type == 'purpose' ? device.color : 'warning',
									icon: 'fas fa-coins',
									iconColor: SystemStyles.COLOR_CHART_MONEY_VALUE,
								}}
								progress={{
									percent: (device.consumption / result.report.total) * 100,
									title: device.consumptionTitle,
									color: type == 'purpose' ? device.color : 'primary',
									showPercentBar: type != 'peakPower',
									icon: 'fas fa-bolt',
									iconColor: SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY,
									progressColor: 'gradient-chart-consumed-energy',
								}}
								actionBtn={device.actionBtn}
								on:click={() => {
									if (/^(peakPower|device)$/.test(type)) {
										onDeviceClick(device.id);
									}
								}}
							/>
						{/if}
					{/each}
					{#each reversedData as device}
						{#if device.reversed > 0}
							<ListItem
								title={device.title}
								image={device.image}
								backgroundColor={device.backgroundColor}
								viewType={device.type}
								subtitle={device.time}
								progress={{
									percent:
										(device.reversed / Math.abs(result.report.totalReversed)) * 100,
									title: device.reversedTitle,
									color: 'solar',
									showPercentBar: type != 'peakPower',
									icon: 'fas fa-bolt',
									iconColor: SystemStyles.COLOR_SOLAR,
								}}
								actionBtn={device.actionBtn}
								on:click={() => {
									if (/^(peakPower|device)$/.test(type)) {
										onDeviceClick(device.id);
									}
								}}
							/>
						{/if}
					{/each}
				</Stack>
			</Scroll>
		</svelte:fragment>
	</ControlsContainer>
{/if}
